import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";

const EntriesPagination = props => {
  const itemsPerPageRef = useRef(0);

  const [currPage, setCurrPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [pagesParts, setPagesParts] = useState(1);
  const [currPagePart, setCurrPagePart] = useState(1);
  const [pageStartingPoint, setPageStartingPoint] = useState(1);

  const PARTS_PAGES_LIMIT = 5;

  useEffect(() => {
    if (props.itemsPerPage >= props.totalRecords) {
      setPages(1);
      setCurrPage(1);
    } else {
      const pages = Math.ceil(
        parseInt(props.totalRecords) / parseInt(props.itemsPerPage)
      );
      const pagesParts = Math.ceil(pages / PARTS_PAGES_LIMIT);
      setPages(pages);
      setPagesParts(pagesParts);

      if (currPage > pages) {
        updatePagePart(pagesParts);
        updatePage(pages);
      }
    }

    if (props.currPage === 0) {
      setCurrPage(1);
    }

    itemsPerPageRef.current = props.itemsPerPage;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.itemsPerPage, props.totalRecords, props.currPage]);

  // restore the previous page
  useEffect(() => {
    if (props.currPage !== 0) {
      let pgNo = props.currPage / props.itemsPerPage + 1;
      updatePagePart(Math.ceil(pgNo / 5));
      updatePage(pgNo);
    } else {
      updatePagePart(1);
      updatePage(1);
    }
  }, [props.currPage]);

  useEffect(() => {
    let pgNo = props.currPage / props.itemsPerPage + 1;
    updatePagePart(Math.ceil(pgNo / 5));
    updatePage(pgNo);
  }, []);

  const updatePage = pageNo => {
    setCurrPage(pageNo);
    props.paginationUpdated((pageNo - 1) * props.itemsPerPage);
  };

  const updatePagePart = partNo => {
    setCurrPagePart(partNo);
    let startPoint = partNo;
    if (startPoint - 1 !== 0) {
      startPoint = startPoint - 1;
      startPoint = startPoint * 5;
      startPoint++;
    }
    setPageStartingPoint(startPoint);
    updatePage(startPoint);
  };

  const switchPage = pageNo => {
    if (pageNo < pageStartingPoint) {
      updatePagePart(currPagePart - 1);
    } else if (pageNo >= pageStartingPoint + 5) {
      updatePagePart(currPagePart + 1);
    }
    updatePage(pageNo);
  };

  const renderPages = () => {
    let pag = [];
    for (
      let i = pageStartingPoint;
      i < pageStartingPoint + 5 && i <= pages;
      i++
    ) {
      pag.push(
        <PaginationItem key={i} active={i === currPage}>
          <PaginationLink onClick={e => updatePage(i)}>{i}</PaginationLink>
        </PaginationItem>
      );
    }

    return pag;
  };

  return (
    <div
      className={
        props.alignInOneLine === true
          ? "d-flex flex-row justify-content-between"
          : ""
      }
    >
      {props.showNoTotalCount === false && (
        <p className="mb-0">
          {props.totalRecords === 0 && "Showing 0 to 0 of 0 entries"}
          {props.totalRecords !== 0 &&
            props.itemsPerPage >= props.totalRecords &&
            `Showing 1
          to
          ${props.totalRecords} of ${props.totalRecords}
          entries`}
          {props.totalRecords !== 0 &&
            props.itemsPerPage < props.totalRecords &&
            `Showing ${currPage === 1 ? 1 : props.itemsPerPage * (currPage - 1)}
          to 
          ${
            props.itemsPerPage * currPage > props.totalRecords
              ? props.totalRecords
              : props.itemsPerPage * currPage
          } of ${props.totalRecords}
          entries`}
        </p>
      )}

      <div
        style={{ overflow: "auto" }}
        className="pagination-container d-flex justify-content-end"
      >
        {props.totalRecords !== 0 && (
          <Pagination aria-label="Page navigation">
            <PaginationItem disabled={currPage === 1}>
              <PaginationLink
                first
                onClick={e => {
                  updatePage(1);
                  updatePagePart(1);
                }}
              />
            </PaginationItem>
            <PaginationItem disabled={currPage === 1}>
              <PaginationLink
                previous
                onClick={e => switchPage(currPage - 1)}
              />
            </PaginationItem>
            {currPagePart !== 1 && (
              <PaginationItem>
                <PaginationLink onClick={e => updatePagePart(currPagePart - 1)}>
                  ...
                </PaginationLink>
              </PaginationItem>
            )}

            {renderPages()}
            {currPagePart !== pagesParts && currPage !== pages && (
              <PaginationItem>
                <PaginationLink onClick={e => updatePagePart(currPagePart + 1)}>
                  ...
                </PaginationLink>
              </PaginationItem>
            )}
            <PaginationItem disabled={currPage === pages}>
              <PaginationLink next onClick={e => switchPage(currPage + 1)} />
            </PaginationItem>
            <PaginationItem disabled={currPage === pages}>
              <PaginationLink
                last
                onClick={e => {
                  updatePagePart(pagesParts);
                  updatePage(pages);
                }}
              />
            </PaginationItem>
          </Pagination>
        )}
      </div>
    </div>
  );
};

export default EntriesPagination;

EntriesPagination.defaultProps = {
  showNoTotalCount: false,
  alignInOneLine: false,
  itemsPerPage: 10,
  totalRecords: 10
};

EntriesPagination.prototype = {
  showNoTotalCount: PropTypes.bool,
  alignInOneLine: PropTypes.bool,
  itemsPerPage: PropTypes.number.isRequired,
  totalRecords: PropTypes.number.isRequired,
  paginationUpdated: PropTypes.func.isRequired,
  currPage: PropTypes.number.isRequired
};
